import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Hotel, Check, X, Clock, Mail, Send, Users, Euro, Tent, TreePine, Gift, Trash2 } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getAllAccommodations, updateAccommodation, deleteAccommodation, confirmAccommodation, getAdminMe, getAdminConfig } from '@/lib/api';

// Pricing constants
const LODGING_PRICES = {
  chambre: 50,  // Chambre domaine = 50€/personne
  cabane: 35,   // Cabane = 35€/personne
  tente: 30     // Tente = 30€/personne
};

const LODGING_LABELS = {
  chambre: 'Chambre',
  cabane: 'Cabane',
  tente: 'Tente'
};

const AdminAccommodations = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accommodations, setAccommodations] = useState([]);
  const [config, setConfig] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, acc: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, acc: null });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const [accResponse, configResponse] = await Promise.all([
        getAllAccommodations(),
        getAdminConfig()
      ]);
      setAccommodations(accResponse.data);
      setConfig(configResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (accId, newStatus) => {
    try {
      await updateAccommodation(accId, { status: newStatus });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLodgingChange = async (acc, newLodging) => {
    try {
      const numAdults = acc.num_adults || (acc.rsvp?.adults?.length || 0);
      const numChildren = acc.num_children || (acc.rsvp?.children?.length || 0);
      const calculatedAmount = newLodging ? LODGING_PRICES[newLodging] * numAdults : 0;
      
      await updateAccommodation(acc.id, { 
        assigned_lodging: newLodging,
        num_adults: numAdults,
        num_children: numChildren,
        calculated_amount: calculatedAmount
      });
      toast.success(`Logement attribué: ${LODGING_LABELS[newLodging]} - ${calculatedAmount}€`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleSendConfirmation = async () => {
    if (!confirmDialog.acc) return;
    setSending(true);
    try {
      const response = await confirmAccommodation(confirmDialog.acc.id);
      toast.success(`Email de confirmation envoyé à ${response.data.email}`);
      setConfirmDialog({ open: false, acc: null });
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleShouldPayChange = async (acc, shouldPay) => {
    try {
      await updateAccommodation(acc.id, { should_pay: shouldPay });
      toast.success(shouldPay ? 'Invité à payer' : 'Hébergement offert');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.acc) return;
    try {
      await deleteAccommodation(deleteDialog.acc.id);
      toast.success('Hébergement supprimé');
      setDeleteDialog({ open: false, acc: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredAccommodations = accommodations.filter((acc) => {
    if (filterType !== 'all' && acc.accommodation_type !== filterType) return false;
    if (filterStatus !== 'all' && acc.status !== filterStatus) return false;
    return true;
  });

  const pricePerPerson = config?.price_per_person || 65;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" /> Confirmé</Badge>;
      case 'waitlist':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" /> Liste d'attente</Badge>;
      case 'cancelled':
        return <Badge variant="secondary"><X className="w-3 h-3 mr-1" /> Annulé</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Gestion des hébergements">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  const domaineAccommodations = accommodations.filter(a => a.accommodation_type === 'domaine');
  const totalPeopleDomaine = domaineAccommodations.reduce((sum, a) => sum + (a.num_people || 0), 0);
  
  // Calculate total based on assigned lodgings
  const totalEstimatedAmount = domaineAccommodations.reduce((sum, a) => {
    if (a.assigned_lodging && LODGING_PRICES[a.assigned_lodging]) {
      const numAdults = a.num_adults || (a.rsvp?.adults?.length || 0);
      return sum + (LODGING_PRICES[a.assigned_lodging] * numAdults);
    }
    return sum + (a.calculated_amount || 0);
  }, 0);

  // Helper to get lodging display
  const getLodgingIcon = (lodging) => {
    switch (lodging) {
      case 'chambre': return <Home className="w-4 h-4" />;
      case 'cabane': return <TreePine className="w-4 h-4" />;
      case 'tente': return <Tent className="w-4 h-4" />;
      default: return null;
    }
  };

  // Calculate amount for display
  const calculateAmount = (acc) => {
    if (acc.assigned_lodging && LODGING_PRICES[acc.assigned_lodging]) {
      const numAdults = acc.num_adults || (acc.rsvp?.adults?.length || 0);
      return LODGING_PRICES[acc.assigned_lodging] * numAdults;
    }
    return acc.calculated_amount || 0;
  };

  return (
    <AdminLayout title="Gestion des hébergements">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Home className="w-8 h-8 text-terracotta" />
            <div>
              <p className="text-sm text-gray-500">Domaine</p>
              <p className="text-2xl font-bold">{domaineAccommodations.length}</p>
              <p className="text-xs text-gray-400">{totalPeopleDomaine} personnes</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Hotel className="w-8 h-8 text-terracotta" />
            <div>
              <p className="text-sm text-gray-500">Hôtel B&B</p>
              <p className="text-2xl font-bold">{accommodations.filter(a => a.accommodation_type === 'hotel').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-2xl font-bold">{accommodations.filter(a => a.status === 'pending').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Euro className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Total estimé</p>
              <p className="text-2xl font-bold text-green-600">{totalEstimatedAmount}€</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Processus de confirmation pour le Domaine :</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li>L'invité fait une demande d'hébergement (statut "En attente")</li>
              <li>Vous vérifiez la disponibilité et cliquez sur "Envoyer confirmation"</li>
              <li>Un email est envoyé avec les instructions de paiement (avant le 30 juin 2026)</li>
              <li>Une fois le paiement reçu, validez-le dans la section Paiements</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="filter-type" className="w-full md:w-40">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="domaine">Domaine</SelectItem>
              <SelectItem value="hotel">Hôtel</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-full md:w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="confirmed">Confirmé</SelectItem>
              <SelectItem value="waitlist">Liste d'attente</SelectItem>
              <SelectItem value="cancelled">Annulé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Famille</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Type demandé</TableHead>
                <TableHead className="text-center">Adultes</TableHead>
                <TableHead className="text-center">Enfants</TableHead>
                <TableHead>Préférence invité</TableHead>
                <TableHead>Logement attribué</TableHead>
                <TableHead className="text-center">Faire payer</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccommodations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                    Aucune demande d'hébergement
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccommodations.map((acc) => {
                  const numAdults = acc.num_adults || (acc.rsvp?.adults?.length || 0);
                  const numChildren = acc.num_children || (acc.rsvp?.children?.length || 0);
                  const amount = calculateAmount(acc);
                  const preferredLodging = acc.preferred_lodging;
                  
                  // Labels pour les préférences
                  const PREF_LABELS = {
                    chambre: { label: 'Chambre', color: 'text-blue-600' },
                    cabane: { label: 'Cabane', color: 'text-green-600' },
                    tente: { label: 'Tente cloche', color: 'text-amber-600' },
                    tente_perso: { label: 'Tente perso', color: 'text-emerald-600' }
                  };
                  
                  return (
                    <TableRow key={acc.id}>
                      <TableCell className="font-medium">
                        {acc.rsvp?.family_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {acc.rsvp?.email || '-'}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          {acc.accommodation_type === 'domaine' ? (
                            <><Home className="w-4 h-4 text-terracotta" /> Domaine</>
                          ) : (
                            <><Hotel className="w-4 h-4 text-terracotta" /> Hôtel</>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="flex items-center justify-center gap-1 font-medium">
                          <Users className="w-4 h-4 text-gray-400" />
                          {numAdults}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-gray-500">{numChildren > 0 ? numChildren : '-'}</span>
                        {numChildren > 0 && <span className="text-xs text-green-600 block">Gratuit</span>}
                      </TableCell>
                      <TableCell>
                        {preferredLodging && PREF_LABELS[preferredLodging] ? (
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${PREF_LABELS[preferredLodging].color}`}>
                              {PREF_LABELS[preferredLodging].label}
                            </span>
                            {preferredLodging !== 'tente_perso' && (
                              <span className="text-xs text-gray-500">
                                {LODGING_PRICES[preferredLodging]}€ × {numAdults} = {LODGING_PRICES[preferredLodging] * numAdults}€
                              </span>
                            )}
                            {preferredLodging === 'tente_perso' && (
                              <span className="text-xs text-emerald-600">Gratuit</span>
                            )}
                            {acc.bring_own_tent && <span className="text-xs text-emerald-600">🏕️ Tente perso</span>}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Non spécifié</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {acc.accommodation_type === 'domaine' ? (
                          <Select
                            value={acc.assigned_lodging || ""}
                            onValueChange={(value) => handleLodgingChange(acc, value)}
                          >
                            <SelectTrigger 
                              data-testid={`lodging-select-${acc.id}`} 
                              className={`w-36 ${acc.assigned_lodging ? 'border-green-500' : 'border-amber-400'}`}
                            >
                              <SelectValue placeholder="Attribuer..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="chambre">
                                <span className="flex items-center gap-2">
                                  <Home className="w-4 h-4" /> Chambre (50€)
                                </span>
                              </SelectItem>
                              <SelectItem value="cabane">
                                <span className="flex items-center gap-2">
                                  <TreePine className="w-4 h-4" /> Cabane (35€)
                                </span>
                              </SelectItem>
                              <SelectItem value="tente">
                                <span className="flex items-center gap-2">
                                  <Tent className="w-4 h-4" /> Tente (30€)
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {acc.accommodation_type === 'domaine' ? (
                          <div className="flex flex-col items-center gap-1">
                            <Checkbox
                              id={`pay-${acc.id}`}
                              checked={acc.should_pay !== false}
                              onCheckedChange={(checked) => handleShouldPayChange(acc, checked)}
                              className="data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta"
                            />
                            <span className={`text-xs ${acc.should_pay !== false ? 'text-gray-500' : 'text-emerald-600 font-medium'}`}>
                              {acc.should_pay !== false ? 'Oui' : 'Offert'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {acc.accommodation_type === 'domaine' && acc.assigned_lodging ? (
                          acc.should_pay !== false ? (
                            <div className="flex flex-col items-end">
                              <span className="text-green-600">{amount}€</span>
                              <span className="text-xs text-gray-500">{LODGING_PRICES[acc.assigned_lodging]}€ × {numAdults}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 justify-end text-emerald-600">
                              <Gift className="w-4 h-4" />
                              <span className="font-medium">Offert</span>
                            </div>
                          )
                        ) : acc.accommodation_type === 'domaine' ? (
                          <span className="text-amber-500">En attente</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(acc.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {/* Send confirmation button for domaine pending with assigned lodging */}
                          {acc.accommodation_type === 'domaine' && acc.status === 'pending' && acc.assigned_lodging && (
                            <Button
                              data-testid={`btn-confirm-${acc.id}`}
                              size="sm"
                              onClick={() => setConfirmDialog({ open: true, acc })}
                              className="bg-terracotta hover:bg-terracotta/90"
                            >
                              <Send className="w-4 h-4 mr-1" /> Confirmer
                            </Button>
                          )}
                          {/* Status select */}
                          <Select
                            value={acc.status}
                            onValueChange={(value) => handleStatusChange(acc.id, value)}
                          >
                            <SelectTrigger data-testid={`status-select-${acc.id}`} className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">En attente</SelectItem>
                              <SelectItem value="confirmed">Confirmé</SelectItem>
                              <SelectItem value="waitlist">Liste d'attente</SelectItem>
                              <SelectItem value="cancelled">Annulé</SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Delete button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, acc })}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-4">
        {filteredAccommodations.length} demande(s) d'hébergement
      </p>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, acc: confirmDialog.acc })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-terracotta" />
              Envoyer l'email de confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {(() => {
              const acc = confirmDialog.acc;
              const numAdults = acc?.num_adults || (acc?.rsvp?.adults?.length || 0);
              const numChildren = acc?.num_children || (acc?.rsvp?.children?.length || 0);
              const lodgingType = acc?.assigned_lodging;
              const pricePerAdult = lodgingType ? LODGING_PRICES[lodgingType] : 0;
              const totalAmount = pricePerAdult * numAdults;
              const shouldPay = acc?.should_pay !== false;
              
              return (
                <>
                  <p className="text-gray-700 mb-4">
                    Vous allez envoyer un email de confirmation à <strong>{acc?.rsvp?.family_name}</strong> pour leur hébergement au Domaine.
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Destinataire</span>
                      <span className="font-medium">{acc?.rsvp?.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Logement attribué</span>
                      <span className="font-medium flex items-center gap-2">
                        {getLodgingIcon(lodgingType)} {LODGING_LABELS[lodgingType] || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Adultes</span>
                      <span className="font-medium">{numAdults}</span>
                    </div>
                    {numChildren > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Enfants (gratuit)</span>
                        <span className="font-medium text-green-600">{numChildren}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span className="text-gray-500">Montant</span>
                      {shouldPay ? (
                        <span className="font-bold text-green-600">{totalAmount}€</span>
                      ) : (
                        <span className="font-bold text-emerald-600 flex items-center gap-1">
                          <Gift className="w-4 h-4" /> Offert
                        </span>
                      )}
                    </div>
                  </div>

                  {shouldPay ? (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <p><strong>L'email contiendra :</strong></p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Confirmation de la disponibilité</li>
                        <li>Type de logement : {LODGING_LABELS[lodgingType]}</li>
                        <li>Montant à régler ({pricePerAdult}€ × {numAdults} adulte{numAdults > 1 ? 's' : ''} = {totalAmount}€)</li>
                        <li>Date limite de paiement : 30 juin 2026</li>
                        <li>Coordonnées de paiement (PayPal, Paylib)</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                      <p><strong>🎁 Hébergement offert</strong></p>
                      <p className="mt-1">L'email indiquera que les frais d'hébergement sont pris en charge par les mariés.</p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Confirmation de la disponibilité</li>
                        <li>Type de logement : {LODGING_LABELS[lodgingType]}</li>
                        <li><strong>Aucun paiement demandé</strong></li>
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, acc: null })}>
              Annuler
            </Button>
            <Button 
              data-testid="btn-send-confirmation"
              onClick={handleSendConfirmation}
              disabled={sending}
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {sending ? 'Envoi...' : 'Envoyer l\'email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, acc: deleteDialog.acc })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer cet hébergement ?
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer la demande d'hébergement de <strong>{deleteDialog.acc?.rsvp?.family_name}</strong> ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, acc: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAccommodations;
